import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import Downshift from 'downshift';
import ChevronDown from 'assets/svg/chevron-down.svg';
import ChevronUp from 'assets/svg/chevron-up.svg';
import { MadadParticipantFilterItem } from 'store/state/domainData/selectors/madadParticipants';
import { Text } from 'ds/components/typography';
import { useLocale } from 'locale';
import { ShowAt } from 'consts/breakpoints';
import { ItemListContainer } from 'ds/components/input/Select';

interface CityDropdownProps {
  filterRecords: MadadParticipantFilterItem[];
  value?: string;
  onChange: (docId: string) => void;
  onDropdownOpen?: () => void;
  onOptionSelect?: (label: string) => void;
}

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 376px;
  margin: 0 auto;
`;

const TriggerButton = styled.button`
  width: 100%;
  border: 2px solid ${({ theme }) => theme.colors.neutrals.grey1};
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  border-radius: 16px;
  min-height: 48px;
  padding: ${({ theme }) => theme.spacing(0, 2, 0, 1.5)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
`;

const TriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownWrapper = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  border-radius: 24px;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  box-shadow: ${({ theme }) => theme.shadow.level7};
  z-index: 5;
  overflow: hidden;
`;

const Dropdown = styled.div`
  max-height: 230px;
  padding: ${({ theme }) => theme.spacing(1)};
  overflow-y: auto;
`;

const DropdownItem = styled.button<{ highlighted: boolean }>`
  width: 100%;
  text-align: initial;
  border: none;
  background: ${({ highlighted, theme }) => highlighted ? theme.colors.neutrals.grey10 : theme.colors.neutrals.white};
  padding: ${({ theme }) => theme.spacing(1)};
  border-radius: 8px;
  cursor: pointer;
`;

export const CityDropdown: React.FC<CityDropdownProps> = ({ filterRecords, value, onChange, onDropdownOpen, onOptionSelect }) => {
  const items = useMemo(() => filterRecords.map(({ city }) => city), [ filterRecords ]);
  const { t } = useLocale();

  const selectedItemByValue = useMemo(() => {
    const foundItem = items.find((item) => item.value === value);
    return foundItem || null;
  }, [ items, value ]);

  const itemToString = (item: { label: string }) => item && item.label ? item.label : '';

  return (
    <Downshift
      selectedItem={selectedItemByValue}
      itemToString={itemToString}
      onStateChange={(changes) => {
        if (changes.isOpen === true && onDropdownOpen) {
          onDropdownOpen();
        }
      }}
      onChange={(item) => {
        if (item) {
          if (onOptionSelect) onOptionSelect(item.label);
          onChange(`${item.value}`);
        }
      }}
    >
      {(renderProps) => {
        const { isOpen, getRootProps, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex, selectedItem } = renderProps;
        return (
          <Wrapper {...getRootProps({ refKey: 'ref' }, { suppressRefError: true })}>
            <TriggerButton type="button" {...getToggleButtonProps()}>
              <TriggerLabel>
                <Text>{selectedItem ? selectedItem.label : t('madad.2026.cityDropPlaceholder')}</Text>
              </TriggerLabel>
              {isOpen ? <ChevronUp /> : <ChevronDown />}
            </TriggerButton>
            {isOpen ? (
              <ShowAt at={1}>
                {isMobile => isMobile ? (
                  <ItemListContainer
                    valueExtractor={item => String(item.value)}
                    labelExtractor={item => item.label}
                    valueComparator={(value1, value2) => value1 === value2}
                    selectionPromptText={t('madad.2026.cityDropPlaceholder')}
                    items={items}
                    renderItem={item => <Text>{item.label}</Text>}
                    fullScreenMobileMenu={false}
                    menuZIndex={1002}
                    {...renderProps}
                  />
                ) : (
                  <DropdownWrapper>
                    <Dropdown {...getMenuProps()}>
                      {items.map((item, index) => (
                        <DropdownItem
                          key={`${item.value}`}
                          type="button"
                          {...getItemProps({ item, index })}
                          highlighted={highlightedIndex === index}
                        >
                          <Text>{item.label}</Text>
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </DropdownWrapper>
                )}
              </ShowAt>
            ) : null}
          </Wrapper>
        );
      }}
    </Downshift>
  );
};
